/**
 * Si algo falla al arrancar o al dibujar la app, en vez de una pantalla en negro se muestra qué pasó,
 * con "Reintentar" y el detalle para soporte. Los datos del equipo no se tocan.
 */
import { Component, type ReactNode } from "react";
import { initPlatform, platform } from "@/platform";
import { env } from "@/lib/env";

/** Falla típica justo después de publicar una versión: el navegador tenía la anterior y busca archivos que ya no están. */
export function isStaleBuildError(e: unknown): boolean {
  const m = String((e as Error)?.message ?? e);
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Failed to load module script|ChunkLoadError|Unable to preload CSS/i.test(m);
}

/**
 * Recarga con la versión nueva: borra la copia guardada por el navegador (service worker y su caché; los
 * datos del equipo NO se tocan) y recarga. Una sola vez por minuto, para no quedar en un ciclo.
 */
export async function reloadFresh(force = false, path?: string): Promise<boolean> {
  try {
    const last = Number(sessionStorage.getItem("sofia.freshReload") ?? 0);
    if (!force && Date.now() - last < 60_000) return false;
    sessionStorage.setItem("sofia.freshReload", String(Date.now()));
  } catch { /* sin almacenamiento: se recarga igual */ }
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
    await Promise.all(regs.map((r) => r.unregister()));
  } catch { /* sin service worker */ }
  try {
    const keys = (await globalThis.caches?.keys?.()) ?? [];
    await Promise.all(keys.map((k) => caches.delete(k)));
  } catch { /* sin caché */ }
  // 1.105.0: se pide la página con una dirección nueva (?_r=…): ninguna copia guardada (del navegador, del service
  // worker que todavía controla la pestaña o de la red) puede devolver la versión anterior, cuyos archivos ya no están.
  try { const u = new URL(path ?? location.href, location.href); u.searchParams.set("_r", String(Date.now())); location.replace(u.toString()); }
  catch { location.reload(); }
  return true;
}
/** Olvida la pantalla guardada para volver al abrir (si esa pantalla falla, no queda en un ciclo de error). */
export function forgetLastRoute() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith("sofia.draft.") && k.endsWith(".route")) localStorage.removeItem(k);
    }
  } catch { /* sin almacenamiento */ }
}
// Para el botón Reintentar de la pantalla de error (HTML sin React): vuelve al inicio y recarga.
(globalThis as unknown as { sofiaReloadFresh?: () => void }).sofiaReloadFresh = () => { forgetLastRoute(); void reloadFresh(true, "/"); };

/**
 * Actualización de rescate: si la app no puede abrir (o una pantalla falla), busca una versión nueva y la
 * instala sola, sin necesidad de iniciar sesión. Así un error de una versión se arregla con la siguiente.
 */
let rescuing = false;
export async function rescueUpdate(): Promise<void> {
  if (rescuing) return;
  rescuing = true;
  const say = (t: string) => { const b = document.getElementById("sofia-rescue"); if (b) b.textContent = t; };
  try {
    if (env.platform !== "web" && platform.kind === "web") await initPlatform().catch(() => undefined);
    say("Buscando una versión nueva…");
    const v = await platform.updater.check();
    if (!v) { say(`Tenés la última versión disponible para este equipo (${env.version}).`); return; }
    say(`Versión ${v.version} encontrada: descargando…`);
    const hooks = {
      log: () => undefined,
      progress: (got: number, total: number | null) => say(`Descargando la versión ${v.version}… ${total ? `${Math.min(100, Math.round((got * 100) / total))} %` : `${(got / 1048576).toFixed(1)} MB`}`),
    };
    await platform.updater.download(hooks);
    say(`Instalando la versión ${v.version}: la app se cierra y se vuelve a abrir sola…`);
    await platform.updater.apply(hooks);
  } catch (e) {
    say(`No se pudo actualizar sola (${(e as Error)?.message ?? String(e)}). Revisá internet y tocá "Buscar actualización", o descargá el instalador desde appss.info/descargas.`);
  } finally { rescuing = false; }
}
(globalThis as unknown as { sofiaRescueUpdate?: () => void }).sofiaRescueUpdate = () => { void rescueUpdate(); };

/**
 * Diagnóstico para soporte cuando una pantalla no puede cargar su archivo ("Failed to fetch dynamically
 * imported module"): revisa ese archivo y los que usa (estado, tipo y tamaño) y lo agrega al detalle.
 */
export async function diagnoseImport(message: string): Promise<string | null> {
  const first = message.match(/(https?:\/\/[^\s"']+\.js)/)?.[1];
  if (!first) return null;
  const seen = new Set<string>(); const queue = [first]; const bad: string[] = []; let ok = 0;
  while (queue.length && seen.size < 80) {
    const url = queue.shift()!;
    if (seen.has(url)) continue;
    seen.add(url);
    const name = url.split("/").pop();
    try {
      const r = await fetch(url, { cache: "no-store" });
      const ct = r.headers.get("content-type") ?? "(sin tipo)";
      const text = await r.text();
      if (!r.ok || !/javascript|ecmascript/i.test(ct)) bad.push(`${name}: ${r.status} ${ct} ${text.length} bytes · empieza con ${JSON.stringify(text.slice(0, 40))}`);
      else ok++;
      for (const m of text.matchAll(/(?:from|import)\s*\(?\s*["'](\.{1,2}\/[^"']+?\.js)["']/g)) queue.push(new URL(m[1]!, url).href);
    } catch (e) { bad.push(`${name}: no se pudo pedir (${(e as Error)?.message ?? e})`); }
  }
  return bad.length ? `Archivos con problema (${bad.length} de ${seen.size}):\n${bad.join("\n")}` : `Los ${ok} archivos llegan bien (sin caché): el problema es la copia guardada o al leerlos.`;
}
export async function showDiagnosis(message: string) {
  const d = await diagnoseImport(message).catch(() => null);
  const pre = document.querySelector<HTMLPreElement>("#sofia-fatal-detail");
  if (d && pre) pre.textContent += `\n\n${d}`;
  // 1.69.1: el error se manda solo al administrador del sitio (en la app y por email).
  void reportError(message, pre?.textContent ?? `${message}${d ? `\n\n${d}` : ""}`);
}

/**
 * Manda el error al servidor (función error-report), una vez por hora por cada error en este equipo.
 * Sirve sin sesión. Si no hay internet, queda guardado y se manda al volver a abrir la app.
 */
const REPORTED = "sofia.errReported";
const PENDING_ERR = "sofia.errPending";
export async function reportError(message: string, detail: string): Promise<void> {
  const key = `${env.version}|${message.split("\n")[0]!.slice(0, 200)}`;
  let sent: Record<string, number> = {};
  try { sent = JSON.parse(localStorage.getItem(REPORTED) ?? "{}") as Record<string, number>; } catch { /* sin almacenamiento */ }
  if (sent[key] && Date.now() - sent[key]! < 3600_000) return;
  let device: { businessId?: string; prefix?: string } | null = null;
  try { device = (await import("@/modules/devices/device-state")).deviceState.get() as unknown as { businessId?: string; prefix?: string } | null; } catch { /* sin estado */ }
  const body = { message: message.slice(0, 2000), detail: detail.slice(0, 12000), version: env.version, platform: env.platform,
    route: (location.hash || location.pathname).slice(0, 300), businessId: device?.businessId ?? null, devicePrefix: device?.prefix ?? null };
  try {
    const { supabase } = await import("@/lib/supabase");
    const { error } = await supabase.functions.invoke("error-report", { body });
    if (error) throw error;
    sent[key] = Date.now();
    const keep = Object.fromEntries(Object.entries(sent).filter(([, t]) => Date.now() - t < 86400_000));
    try { localStorage.setItem(REPORTED, JSON.stringify(keep)); localStorage.removeItem(PENDING_ERR); } catch { /* sin almacenamiento */ }
  } catch {
    try { localStorage.setItem(PENDING_ERR, JSON.stringify(body)); } catch { /* sin almacenamiento */ }
  }
}
/** Al abrir la app: manda el error que quedó guardado sin internet. */
export function flushPendingError() {
  try {
    const raw = localStorage.getItem(PENDING_ERR);
    if (!raw) return;
    const b = JSON.parse(raw) as { message: string; detail: string };
    void reportError(b.message, b.detail);
  } catch { /* sin almacenamiento */ }
}

/**
 * 1.93.0: los botones de la pantalla de error se atienden con un listener (no con onclick="…" en el HTML):
 * la política de seguridad de la web (CSP) no deja correr código escrito dentro del HTML.
 */
let fatalActions = false;
function installFatalActions() {
  if (fatalActions || typeof document === "undefined") return;
  fatalActions = true;
  document.addEventListener("click", (ev) => {
    const b = (ev.target as Element | null)?.closest?.("[data-sofia-fatal]");
    if (!b) return;
    const act = b.getAttribute("data-sofia-fatal");
    if (act === "retry") { location.hash = "#/"; forgetLastRoute(); void reloadFresh(true); }
    else if (act === "update") void rescueUpdate();
    else if (act === "copy") {
      const pre = b.closest("div")?.parentElement?.querySelector("pre");
      if (pre) void navigator.clipboard?.writeText(pre.innerText).catch(() => { /* sin portapapeles */ });
    }
  });
}

export function fatalHtml(message: string): string {
  installFatalActions();
  const esc = (t: string) => t.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
  return `<div style="min-height:100vh;display:grid;place-items:center;padding:24px;background:#0d1112;color:#e8eeec;font-family:system-ui,sans-serif">
  <div style="max-width:520px;background:#161c1e;border:1px solid #2a3336;border-radius:18px;padding:24px">
    <h2 style="margin:0 0 8px">La app no pudo abrir esta pantalla</h2>
    <p style="margin:0 0 14px;opacity:.8">Los datos del equipo están bien guardados. Tocá <b>Reintentar</b>. El detalle se manda solo a soporte para revisarlo; si vuelve a pasar, podés escribirnos por Mensajes o WhatsApp.</p>
    <pre id="sofia-fatal-detail" style="white-space:pre-wrap;background:#0d1112;border-radius:10px;padding:10px;font-size:12px;max-height:180px;overflow:auto">${esc(message)}</pre>
    <div id="sofia-rescue" style="margin-top:12px;font-size:13px;opacity:.85"></div>
    <div style="display:flex;gap:8px;margin-top:14px;flex-wrap:wrap">
      <button data-sofia-fatal="retry" style="padding:10px 16px;border-radius:10px;border:0;background:#3ec5a0;color:#062;font-weight:700;cursor:pointer">Reintentar</button>
      <button data-sofia-fatal="update" style="padding:10px 16px;border-radius:10px;border:1px solid #3ec5a0;background:transparent;color:#3ec5a0;font-weight:700;cursor:pointer">Buscar actualización</button>
      <button data-sofia-fatal="copy" style="padding:10px 16px;border-radius:10px;border:1px solid #2a3336;background:transparent;color:inherit;cursor:pointer">Copiar el detalle</button>
    </div>
  </div></div>`;
}

export class FatalBoundary extends Component<{ children: ReactNode; version: string }, { error: Error | null }> {
  declare props: Readonly<{ children: ReactNode; version: string }>;
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error) {
    console.error("Pantalla con error:", error);
    forgetLastRoute();
    // Si hay una versión nueva (quizás con la corrección), se instala sola.
    setTimeout(() => { void rescueUpdate(); void showDiagnosis(`${error.message}`); }, 300);
    // Versión nueva publicada mientras la app estaba abierta: se recarga sola con la nueva.
    if (isStaleBuildError(error)) void reloadFresh();
  }
  render() {
    if (!this.state.error) return this.props.children;
    const e = this.state.error;
    return <div dangerouslySetInnerHTML={{ __html: fatalHtml(`Versión ${this.props.version}\n${e.message}\n${(e.stack ?? "").split("\n").slice(0, 6).join("\n")}`) }} />;
  }
}
